import { LogIn, LogOut, Coffee, Clock, Hourglass, CheckCircle2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { BREAK_OPTIONS, type BreakType, type TodayResponse } from '../types';

interface MyAttendanceCardProps {
  today?: TodayResponse;
  loading: boolean;
  busy: boolean;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onStartBreak: (type: BreakType) => void;
  onEndBreak: () => void;
}

export function MyAttendanceCard({
  today,
  loading,
  busy,
  onCheckIn,
  onCheckOut,
  onStartBreak,
  onEndBreak,
}: MyAttendanceCardProps) {
  const log = today?.log ?? null;
  const activeBreak = today?.activeBreak ?? null;
  const checkedIn = !!log?.check_in;
  const checkedOut = !!log?.check_out;
  const onBreak = !!activeBreak;

  return (
    <Card className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm">
      {loading ? (
        <div className="flex h-36 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-[#6825F5]" />
              <h2 className="text-lg font-bold tracking-tight text-[#11183F]">
                My Attendance — Today
              </h2>
            </div>
            {checkedOut ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-3.5 py-1 text-xs font-semibold text-[#2563EB]">
                <span className="h-2 w-2 rounded-full bg-[#2563EB]" /> Checked Out
              </span>
            ) : onBreak ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-3.5 py-1 text-xs font-semibold text-[#D97706]">
                <span className="h-2 w-2 rounded-full bg-[#D97706] animate-pulse" /> On Break ({activeBreak?.break_type})
              </span>
            ) : checkedIn ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#A7F3D0] bg-[#D1FAE5] px-3.5 py-1 text-xs font-semibold text-[#059669]">
                <span className="h-2 w-2 rounded-full bg-[#10B981]" /> Checked In
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-[#F1F3F9] px-3.5 py-1 text-xs font-semibold text-[#64748B]">
                <span className="h-2 w-2 rounded-full bg-[#94A3B8]" /> Not Checked In
              </span>
            )}
          </div>

          {/* 3 Metrics Block with Vertical Dividers */}
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#F1F3F9] rounded-2xl border border-[#F1F3F9] bg-[#FAFBFD] p-2 md:p-3">
            {/* Check In */}
            <div className="flex items-center gap-4 px-5 py-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EDE9FE] text-[#6825F5]">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <span className="block text-[10px] font-bold tracking-wider text-[#747C9C] uppercase">
                  CHECK IN
                </span>
                <span className="block text-lg font-bold text-[#11183F] mt-0.5">
                  {log?.check_in || '—'}
                </span>
              </div>
            </div>

            {/* Check Out */}
            <div className="flex items-center gap-4 px-5 py-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#DBEAFE] text-[#2563EB]">
                <LogOut className="h-5 w-5" />
              </div>
              <div>
                <span className="block text-[10px] font-bold tracking-wider text-[#747C9C] uppercase">
                  CHECK OUT
                </span>
                <span className="block text-lg font-bold text-[#11183F] mt-0.5">
                  {log?.check_out || '—'}
                </span>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-center gap-4 px-5 py-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EDE9FE] text-[#8B5CF6]">
                <Hourglass className="h-5 w-5" />
              </div>
              <div>
                <span className="block text-[10px] font-bold tracking-wider text-[#747C9C] uppercase">
                  HOURS
                </span>
                <span className="block text-lg font-bold text-[#11183F] mt-0.5">
                  {log?.hours != null && log.hours > 0 ? `${log.hours}h` : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
            {!checkedIn && (
              <div className="flex items-center justify-between w-full">
                <p className="text-xs font-medium text-[#747C9C]">Ready to start your workday?</p>
                <Button
                  onClick={onCheckIn}
                  loading={busy}
                  className="rounded-full bg-[#6825F5] hover:bg-[#581cd6] text-white px-6 py-2.5 font-semibold text-xs flex items-center gap-2 shadow-sm shadow-purple-200 transition-all border-none"
                >
                  <LogIn className="h-4 w-4" /> Check In
                </Button>
              </div>
            )}

            {checkedIn && !checkedOut && (
              <>
                {onBreak ? (
                  <div className="flex flex-wrap items-center justify-between w-full gap-3">
                    <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-800">
                      <Coffee className="h-4 w-4 text-amber-600 animate-pulse" />
                      Currently on {activeBreak?.break_type} break
                    </div>
                    <Button
                      onClick={onEndBreak}
                      loading={busy}
                      className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-2.5 font-semibold text-xs flex items-center gap-2 shadow-sm shadow-emerald-200 transition-all border-none"
                    >
                      <Coffee className="h-4 w-4" /> Get to Work (End Break)
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between w-full gap-3">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-sm font-bold text-[#11183F] mr-1">Break:</span>
                      {BREAK_OPTIONS.map((b) => (
                        <button
                          key={b.value}
                          type="button"
                          disabled={busy}
                          onClick={() => onStartBreak(b.value)}
                          className={
                            b.value === 'lunch'
                              ? 'rounded-full border border-purple-200 bg-[#EDE9FE] text-[#6825F5] font-semibold text-xs px-5 py-2 hover:bg-[#DDD6FE] transition-all disabled:opacity-50 cursor-pointer'
                              : 'rounded-full border border-[#E8E8F5] bg-white text-[#475569] font-medium text-xs px-5 py-2 hover:bg-[#F8F9FE] hover:border-slate-300 transition-all disabled:opacity-50 cursor-pointer'
                          }
                        >
                          {b.label}
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="danger"
                      onClick={onCheckOut}
                      loading={busy}
                      disabled={onBreak}
                      className="rounded-full bg-[#6825F5] hover:bg-[#581cd6] text-white px-6 py-2.5 font-semibold text-xs flex items-center gap-2 shadow-sm shadow-purple-200 transition-all border-none"
                    >
                      <LogOut className="h-4 w-4" /> Check Out
                    </Button>
                  </div>
                )}
              </>
            )}

            {checkedOut && (
              <div className="flex items-center gap-2 text-xs font-medium text-[#747C9C] italic">
                <CheckCircle2 className="h-4 w-4 text-[#10B981]" /> You're done for today. See you tomorrow!
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
